<script setup>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import _ from 'lodash';
  import moment from "moment";
  import { usePageLoadingStore } from "../../../../../stores/pageLoading";
  import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
  import KSAPrimaryButton from "../../../../../components/ui-components/button/KSAPrimaryButton.vue";
  import adminJobFairApplicantService from '../../../services/jobfair/adminJobFairApplicantService';
  import JobFairJobNoticeDetailModal from '../../../../user/components/modal/jobFair/JobFairJobNoticeDetailModal.vue';
  import JobFairResumeDetailModal from '../../../../user/components/modal/jobFair/JobFairResumeDetailModal.vue';
  import mypageJobFairApplicantService from '../../../../user/services/mypage/jobFair/mypageJobFairApplicantService';

  const route = useRoute()
  const router = useRouter()
  const loadingStore = usePageLoadingStore()
  const { $pageListBoardNo, $toast, $interests, $intvGb, $applyStateGb, alert, confirm } = getCurrentInstance().appContext.config.globalProperties;

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const searchOpen = ref(true);
  const list = ref([]);

  const data_interests = ref([...$interests]); // 관심 카테고리
  const data_intvGb = ref([...$intvGb]); // 면접방식
  const data_applyStateGb = ref([...$applyStateGb]); // 서류지원상태

  const jobNoticeModal = ref(null);
  const jobFairResumeModal = ref(null);

  // 검색
  const searchTypeRef = ref(null);
  const interestsListRef = ref(null);
  const applyStateGbsListRef = ref(null);
  const intvsListRef = ref(null);

  // 검색조건 - 날짜검색기준 (all 모든 기간, term 기간 검색)
  const searchDurationTypeGroup = ref([
    { label:'모든 기간', value: 'all' },
    { label:'기간 검색', value: 'term' },
  ])

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: 10 },
    { label:"30개씩 보기", value: 30 },
    { label:"50개씩 보기", value: 50 },
    { label:"100개씩 보기", value: 100 },
  ])

  const searchTypeGroup = ref([
    { label:"이름", value: "applicantName", placeholder: "이름을 입력해주세요."  },
    { label:"이력서 제목", value: "rTitle", placeholder: "이력서 제목을 입력해주세요." },
    { label:"기업명", value: "corpName", placeholder: "기업명을 입력해주세요."  },
    { label:"마스터 이름", value: "name", placeholder: "마스터 이름을 입력해주세요."  },
    { label:"채용공고 제목", value: "jnTitle", placeholder: "채용공고 제목을 입력해주세요."  },
  ]);

  const searchInputPlaceholder = computed(()=>{
    const option = _.find(searchTypeGroup.value, { value: searchInput.value.searchType });
    if(option) {
      return option.placeholder;
    } else {
      return '';
    }
  })

  const initSearchInputModel = { // 학생/취업준비생(2) - 입사지원(3)
    searchInterests: ['설계','장비','소재','제조공정','패키지','테스트','SW','기타'],
    searchApplyStateGbs: ['01', '02', '03', '04', '05', '06'],
    searchIntvGbs: ['01', '02', '03', '04'],
    searchDurationType: 'all',
    startYmd: '',
    endYmd: '',
    searchType: 'applicantName', // applicantName 이름, rTitle 이력서 제목, corpName 기업명, name 마스터 이름, jTitle 채용공고 제목
    searchInput: '',
  };

  // 검색 초기값 설정
  const searchInput = ref({...initSearchInputModel});

  // 검색 - searchType 셀렉트 박스 선택시
  const onSelectSearchTypeGroup = (option) => {
    searchInput.value.searchType = option.value;
  }

  const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
  }

  // 페이지당 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }


  const retrieveList = () => {
    const now = new Date();
    const startYmd = searchInput.value.startYmd ? searchInput.value.startYmd : moment(now).format('YYYYMMDD');
    const endYmd = searchInput.value.endYmd ? searchInput.value.endYmd : moment(new Date(now.setDate(now.getDate() + 30))).format('YYYYMMDD');

    const paramObj = {
      searchInterests: _.map(searchInput.value.searchInterests).join(','),
      searchApplyStateGbs: _.map(searchInput.value.searchApplyStateGbs).join(','),
      searchIntvGbs: _.map(searchInput.value.searchIntvGbs).join(','),
      startYmd: searchInput.value.searchDurationType === 'all' ? '' : startYmd,
      endYmd: searchInput.value.searchDurationType === 'all' ? '' : endYmd,
      searchType: searchInput.value.searchType,
      searchInput: searchInput.value.searchInput,
      pageNumber: currentPage.value,
      size: pageSize.value,
    };

    loadingStore.loading = true;
    adminJobFairApplicantService.retrieveAllApplicantListPaging(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        if (res.data.code.toLowerCase() === "success") {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
      }).catch((res) => {
      loadingStore.loading = false;
      $toast("error", res);
    });
  };

  const onClickSearch = () => {
    currentPage.value = 1;
    retrieveList();
  }

  const clearSearchInput = () => {
    searchInput.value = {...initSearchInputModel};
    interestsListRef.value?.initOptions();
    applyStateGbsListRef.value?.initOptions();
    intvsListRef.value?.initOptions();
    searchTypeRef.value?.initSelectedOption('applicantName');
  }

  // 이력서 보기
  const onClickMyResume = async (resumeId) => {
    await jobFairResumeModal.value.show({
      resumeId,
      showDeleteBtn: false,
    });
  }

  // 채용공고 보기
  const onClickJobNotice = async (jobNoticeSeq, corpId) => {
    await jobNoticeModal.value.show({
      jobNoticeSeq,
      corpId,
    });
  }
    
  // 입사지원 상태변경 joinStateGb
  const onClickApplyStateGb = async (resumeId, jobNoticeSeq, stateGb) => {
    let modalContent, okButtonText;
    if (stateGb === "02") {
      modalContent = ["지원취소 처리하시겠습니까?", "'지원취소'으로 상태가 변경됩니다."];
      okButtonText = "지원취소";
    } else if (stateGb === "03") {
      modalContent = ["서류합격 처리하시겠습니까?", "'서류합격'으로 상태가 변경됩니다."];
      okButtonText = "서류합격";  
    } else if (stateGb === "04") {
      modalContent = ["서류불합격 처리하시겠습니까?", "'서류불합격'으로 상태가 변경됩니다."];
      okButtonText = "서류불합격";
    } else if (stateGb === "05") {
      modalContent = ["최종합격 처리하시겠습니까?", "'최종합격'으로 상태가 변경됩니다."];
      okButtonText = "최종합격";
    } else if (stateGb === "06") {
      modalContent = ["최종불합격 처리하시겠습니까?", "'최종불합격'으로 상태가 변경됩니다."];
      okButtonText = "최종불합격";
    }

    const ok = await confirm(modalContent, okButtonText);
    if(ok) {
      const paramObj = {
        resumeId: resumeId,
        jobNoticeSeq: jobNoticeSeq,
        applyStateGb: stateGb,
      };
      mypageJobFairApplicantService.updateApplyStateGb(paramObj)
        .then( (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            alert("변경하였습니다.");
            retrieveList();
          } else {
            alert("변경을 실패하였습니다.");
          }
        })
        .catch( (res) =>  {
          loadingStore.loading = false;
          alert("변경을 실패하였습니다.");
        });
    }
  }

  const openDetailInNewTab = (linkName, id, query = {}) => {
    const url = router.resolve({
      name: linkName,
      params: { id },
      query,
    }).href;

    window.open(url, '_blank');
  }

  onMounted(() => {
    retrieveList();
  })
</script>

<template>
  <div>
    <!-- s: 검색 -->
    <div class="filter-wrap" :class="{'open': searchOpen}">
      <p @click="searchOpen = !searchOpen">검색</p>
      <div class="filter">
        <dl>
          <dt style="display:block;"><span>관심 카테고리</span><br/><small style="font-weight: normal;">복수 선택 가능</small></dt>
          <dd>
            <KSACheckBoxGroup
              ref="interestsListRef"
              className="mr-md"
              name="interestsList"
              v-model="searchInput.searchInterests"
              :options="data_interests"
            />
          </dd>
        </dl>
        <dl>
          <dt style="display:block;">상태</dt>
          <dd>
            <KSACheckBoxGroup
              ref="applyStateGbsListRef"
              className="mr-md"
              name="applyStateGbsList"
              v-model="searchInput.searchApplyStateGbs"
              :options="data_applyStateGb"
            />
          </dd>
        </dl>
        <dl>
          <dt>면접 방식</dt>
          <dd>
            <KSACheckBoxGroup
              ref="intvsListRef"
              className="mr-md"
              name="intvsList"
              v-model="searchInput.searchIntvGbs"
              :options="data_intvGb"
            />
          </dd>
        </dl>
        <dl>
          <dt>입사지원일</dt>
          <dd class="text-box">
            <div class="grid">
              <template v-for="group in searchDurationTypeGroup" key="item.value" >
                <KSARadioButton class="mr-md" :label="group.label" v-model="searchInput.searchDurationType" :value="group.value" />
              </template>
              <div style="display: inline-block;" v-if="searchInput.searchDurationType !== 'all'">
                <KSADatepickerTerms
                  :startDate="searchInput.startYmd"
                  :endDate="searchInput.endYmd"
                  @update:startDate="(value)=> searchInput.startYmd = value"
                  @update:endDate="(value)=> searchInput.endYmd = value"
                />
              </div>
            </div>
          </dd>
        </dl>
        <!-- applicantName 이름, rTitle 이력서 제목, corpName 기업명, name 마스터 이름, jTitle 채용공고 제목 검색 -->
        <dl>
          <dt>
            <KSASelect ref="searchTypeRef" :options="searchTypeGroup" class="select" @selectOption="onSelectSearchTypeGroup"/>
          </dt>
          <dd>
            <KSAInput class="text-box" v-model="searchInput.searchInput" :placeholder="searchInputPlaceholder" />
          </dd>
        </dl>
        <div class="btn-wrap">
          <KSASecondaryButton class="gray_button" text="초기화" @click="clearSearchInput" />
          <KSAPrimaryButton text="검색" @click="onClickSearch" />
        </div>
      </div>
    </div>
    <!-- e: 검색 -->
    <!-- s: 버튼영역 -->
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <div>
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize" />
      </div>
    </div>
    <!-- e: 버튼영역 -->
    <div class="table-form">
      <table class="horizontal">
        <tr>
          <th>NO</th>
          <th>채용상태</th>
          <th>기업명</th>
          <th>채용공고 제목</th>
          <th>마스터</th>
          <th>관심 카테고리</th>
          <th>면접 방식</th>
          <th>이력서 제목</th>
          <th>이름</th>
          <th>입사 지원일</th>
          <th>상태</th>
          <th>상태변경</th>
        </tr>
        <tr v-for="applicant in list">
          <td>{{ applicant.boardNo }}</td>
          <td>{{ $gbNameKR("JOB_NOTICE_STATE_GB", applicant.jobNoticeStateGb) }}</td>
          <td class="clickable blue text-overflow long-overflow">
            <span @click="openDetailInNewTab('CorporationDetail', applicant.corpId, { tab: 3, tab2: 'job-postings' })">{{ applicant.corpName }}</span>
          </td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(applicant.jobNoticeSeq, applicant.corpId)">{{ applicant.jobName }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', applicant.userId)">{{ applicant.userName }}</span>
          </td>
          <td>{{ applicant.interests }}</td>
          <td>{{ $gbNameKR("INTV_GB", applicant.intvGb) }}</td>
          <td class="text-overflow long-overflow">
            <span class="file blue" v-if="applicant.fileYn === 'Y'" v-for="resumeAttaches in applicant.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
              {{ resumeAttaches.fileName }}
            </span>
            <span class="clickable blue" v-else @click="onClickMyResume(applicant.resumeId)">
              {{ applicant.title }}
            </span>
          </td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', applicant.applicantId)">{{ applicant.applicantName }}</span>
          </td>
          <td>{{ $yyyymmddHHmmss(applicant.createDate) }}</td>
          <td>
            <span v-if="applicant.applyStateGb === '01'">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else-if="applicant.applyStateGb === '03' || applicant.applyStateGb === '05'" class="blue">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else-if="applicant.applyStateGb === '04' || applicant.applyStateGb === '06'" class="red">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else class="gray">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
          </td>
          <td>
            <div v-if="applicant.applyStateGb === '01'">
              <KSATextButton class="clickable black" text="지원취소" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '02')"/>
            </div>
            <div v-else>
              -
            </div>
          </td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        입사지원 내역이 없습니다.
      </div>
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
  <JobFairResumeDetailModal ref="jobFairResumeModal" /> <!-- 특별채용관 이력서 상세 -->
</template>
<style scoped>
</style>
